import type { SyntheticEvent } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { FormattedList, useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import noop from 'lib/function/noop'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import Group from 'components/utils/Group'
import { mediaQueryDown } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import Label from 'LuxKit/Label/Label'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import FormatCurrency from 'components/Common/FormatCurrency'
import { CurrencyProvider } from 'contexts/currencyContext'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import {
  type ManualItineraryDeleteRequest,
  type TransportConnection,
  type TransportOption,
  isChatFlightSavedItemData,
  isMarkedDefaultTransport,
  isCustomFlightSavedItemData,
  isFlightCardSavedItemData,
  isLeFlightSavedItemData,
  isLeCruiseSavedItemData,
} from 'tripPlanner/api/types'
import { useSwitchDefaultTransportMutation } from 'tripPlanner/hooks/api/chat'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { getTransportIcon } from './getTransportIcon'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import FlightSearchDrawer from './FlightSearchDrawer/FlightSearchDrawer'
import ShortlistedFlightsSection from './ShortlistedFlightsSection/ShortlistedFlightsSection'
import { isBookedFlightOption } from './ShortlistedFlightsSection/flightShortlistUtils'
import { getCachedJourney } from './ShortlistedFlightsSection/shortlistedJourneyCache'
import BookedFlightTile from './BookedFlightTile'
import { CANVAS_CONTENT_WIDTH, PLACEHOLDER_TILE_BORDER_WIDTH } from './constants'
import TripPlanTileDashedOutline, { tripPlanTileOutlineHoverStyles } from './TripPlanTileDashedOutline'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useStaffRewind } from 'tripPlanner/assistant/contexts/StaffRewindContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { getFlightSearchDrawerParamsForTransport } from './FlightSearchDrawer/stopFlightSearchDrawerParams'
import { applyRecentFlightSearchDefaults } from './FlightSearchDrawer/flightSearchDrawerParams'
import { isFlightTransportOption, getTransportOptionLabel } from '../utils/transportOptionUtils'
import { buildDefaultTransportSwitchedActivityEntry } from './buildDefaultTransportSwitchedActivityEntry'
import { buildClearedFlightItineraryCommittedStateEntry } from '../hooks/buildSelectedFlightItineraryCommittedStateEntry'
import { buildItemDeletedActivityEntry } from '../utils/buildItemDeletedActivityEntry'
import { compactTransportDuration } from '../utils/formatTransportDuration'
import getLikelyArrivalDateExplanation from './getLikelyArrivalDateExplanation'
import TransportAlternativeLink from './TransportAlternativeLink'
import PlaceholderFlightDateLabel from './PlaceholderFlightDateLabel'
import {
  formatFlightAirportRoute,
  getFlightDateRangeLabel,
  getSavedFlightSummaryDateLabel,
} from './flightTransportDateLabels'

const FLIGHT_TILE_ICON_BACKGROUND = 'rgba(27, 89, 248, 0.2)'
const FLIGHT_TILE_ICON_COLOUR = '#1B59F8'

// Matches the stop sheet's chrome so surfaces that flatten sections into full-bleed
// bands (the mobile timeline) affect the transport leg and the stop together.
const TransportContainer = styled.div`
  max-width: var(--itinerary-canvas-width, ${rem(CANVAS_CONTENT_WIDTH)});
  width: 100%;
  border: var(--trip-plan-canvas-section-border-width, 1px) solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: var(--trip-plan-canvas-section-radius, ${props => props.theme.borderRadius.L});
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding: var(--itinerary-section-padding, ${rem(20)});
`

const TransportContent = styled(Group)`
  min-width: 0;
`

const TransportTile = styled.div<{ $clickable?: boolean; $dashed?: boolean }>`
  position: relative;
  border: ${rem(PLACEHOLDER_TILE_BORDER_WIDTH)} solid ${props => (props.$dashed ? 'transparent' : props.theme.palette.neutral.surface.lightForeground)};
  border-radius: ${props => props.theme.borderRadius.M};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding: ${rem(12)} ${rem(14)};
  ${tripPlanTileOutlineHoverStyles}

  ${props => props.$clickable && css`
    cursor: pointer;

    &:hover {
      border-color: ${props.$dashed ? 'transparent' : props.theme.palette.neutral.surface.lightForegroundAlt};
      box-shadow: ${props.theme.shadow.flat.small};
    }
  `}
`

const TileTrailing = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

/**
 * Icon and summary share a row; the trailing slot drops to its own line under the
 * summary on mobile so the saved count never squeezes the flight details.
 */
const TileLayout = styled.div<{ $stackTrailing?: boolean }>`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  column-gap: ${rem(12)};

  ${props => props.$stackTrailing && css`
    ${mediaQueryDown.mobile} {
      grid-template-columns: auto minmax(0, 1fr);
      row-gap: ${rem(8)};

      ${TileTrailing} {
        grid-column: 2;
        justify-self: start;
      }
    }
  `}
`

const SavedCountLabel = styled(MessagingLabel)``

const MultiCityTotalLabel = styled(Label)`
  align-self: flex-start;
`

const SavedFlightMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`

const SavedFlightMetaItem = styled.div`
  display: inline-flex;
  align-items: center;

  & + &::before {
    content: '·';
    margin: 0 ${rem(6)};
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

const PricePrefix = styled.span`
  margin-inline-end: ${rem(4)};
`

const SavedFlightLuxPlusPrice = styled(PriceRowLuxPlusPrice)`
  display: inline-flex;
`

const IconBadge = styled.div`
  width: ${rem(40)};
  height: ${rem(40)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: ${rem(10)};
  background: color-mix(in srgb, ${props => props.theme.palette.neutral.surface.lightForegroundAlt}, transparent 80%);
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  font-size: ${rem(20)};

  &.flight {
    background: ${FLIGHT_TILE_ICON_BACKGROUND};
    color: ${FLIGHT_TILE_ICON_COLOUR};
  }
`

const BrowseOffersButton = styled(TextButton)`
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};
  padding: ${rem(8)} ${rem(12)};
  line-height: 1;

  &:hover,
  &:focus-visible {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

function formatDuration(
  option: TransportOption,
  intl: ReturnType<typeof useIntl>,
): string | undefined {
  const compactDuration = compactTransportDuration(option.duration)
  if (!compactDuration) return undefined
  return intl.formatMessage({
    id: 'Cb8_HtfJF_lXySd4ifa-',
    defaultMessage: '{duration} (approx)',
    description: 'Compact approximate duration shown beside a trip planner transport mode label',
  }, { duration: compactDuration })
}

export function getFlightTransportDropdownOptions(options: ReadonlyArray<TransportOption>): Array<TransportOption> {
  return options.filter(o => !isFlightCardSavedItemData(o.savedItemData) && !isLeCruiseSavedItemData(o.savedItemData))
}

interface Props {
  transport: TransportConnection
  onRefetchItinerary?: () => void
  onSendMessage?: (message: string) => void
  onDeleteTarget?: (target: ManualItineraryDeleteRequest, displayName: string) => Promise<boolean> | void
  /** Render the return leg of a booked return flight at the end of the itinerary (reversed direction). */
  reversed?: boolean
  /** Heading override (e.g. the reversed "Paris → Sydney" for the return tile). Defaults to transport.name. */
  title?: string
}

function FlightTripPlanTransportLine({ transport, onRefetchItinerary = noop, onSendMessage, onDeleteTarget, reversed = false, title }: Props) {
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const [isFlightDrawerOpen, setIsFlightDrawerOpen] = useState(false)
  const [flightDrawerMountKey, setFlightDrawerMountKey] = useState(0)
  const eventBus = useTripPlannerEventBusOptional()
  const { isInspecting } = useStaffRewind()
  const { mutate: switchDefault, isPending: isSwitching } = useSwitchDefaultTransportMutation()
  const { isStreaming, isStreamOpen } = useChatStreamStatus()
  const { isManualItineraryDeletePending, itineraryData } = useItineraryContext()
  const actionsDisabled = isSwitching || isStreaming || isStreamOpen || isManualItineraryDeletePending

  const defaultOption = useMemo((): TransportOption => {
    const explicitDefault = transport.options.find((o) => isMarkedDefaultTransport(o.savedItemData))
    if (explicitDefault) return explicitDefault
    // No explicit default yet — prefer a flight option when one exists on the leg.
    return transport.options.find(isFlightTransportOption) ?? transport.options[0]
  }, [transport.options])

  const handleSwitchDefaultSuccess = useCallback((newOption: TransportOption | undefined) => {
    onRefetchItinerary()
    if (newOption && !isInspecting) {
      eventBus?.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [buildDefaultTransportSwitchedActivityEntry(defaultOption, newOption)],
      })
    }
  }, [onRefetchItinerary, isInspecting, eventBus, defaultOption])

  const handleSwitchDefaultError = useCallback(() => {
    showSnackbar(intl.formatMessage({
      id: '03GxTY2YYsWsZc8Yxf64',
      defaultMessage: 'Failed to switch transport. Please try again.',
      description: 'Error snackbar message when switching the default transport option fails',
    }), 'critical')
  }, [intl])

  const handleOptionClick = useCallback((itemId: number) => {
    if (itemId === defaultOption.itemId) return
    const newOption = transport.options.find(option => option.itemId === itemId)

    switchDefault(
      { itemId },
      {
        onSuccess: () => handleSwitchDefaultSuccess(newOption),
        onError: handleSwitchDefaultError,
      },
    )
  }, [defaultOption, transport.options, switchDefault, handleSwitchDefaultSuccess, handleSwitchDefaultError])

  const shortlistedFlightsCount = useMemo(
    () => transport.options.filter(o => isFlightCardSavedItemData(o.savedItemData)).length,
    [transport.options],
  )
  const hasShortlistedFlights = shortlistedFlightsCount > 0

  const bookedFlightOption = useMemo(
    () => transport.options.find(o => isFlightCardSavedItemData(o.savedItemData) && isBookedFlightOption(o)),
    [transport.options],
  )
  const otherSavedFlightOptions = useMemo(
    () => transport.options.filter(o => isFlightCardSavedItemData(o.savedItemData) && !isBookedFlightOption(o)),
    [transport.options],
  )

  // Clears the selected-flight-itinerary committed-state fact for this leg once the delete
  // actually succeeds, mirroring useCartPanelActions.handleDelete's own gating -- otherwise the
  // model keeps being told this leg has a selected flight for the rest of its 24h TTL after the
  // customer removes it here. Gated to the currently booked option specifically: this same
  // function also deletes non-booked shortlisted alternatives via ShortlistedFlightsSection (which
  // renders both booked and unbooked groups), and deleting one of those doesn't change what's
  // actually selected for this leg -- clearing unconditionally would tell the model no flight is
  // selected while the real booked flight sits untouched. Only covers this transport line's own
  // item-level delete (targetType: 'item'); a `targetType: 'stop' | 'day'` delete elsewhere can
  // also cascade-remove a flight touching this leg without going through this call site at all --
  // a known, accepted gap (same class as the one this fixes, just larger in scope), not covered here.
  const handleDeleteItem = useCallback(async(itemId: number) => {
    if (actionsDisabled) return
    const isDeletingBookedFlight = bookedFlightOption?.itemId === itemId
    // Captured before the delete request round-trips below, not after.
    const occurredAt = new Date().toISOString()
    const deleted = await onDeleteTarget?.({ targetType: 'item', itemId }, transport.name)
    if (deleted && isDeletingBookedFlight) {
      eventBus?.dispatch({
        type: 'trip_planner_committed_state',
        entries: [buildClearedFlightItineraryCommittedStateEntry(transport)],
      })
      // Gated the same way as the committed-state clear above, and for the same reason: this
      // handler also deletes non-booked shortlisted alternatives (via ShortlistedFlightsSection),
      // and deleting one of those doesn't change what's actually selected for this leg -- reporting
      // it as `transport.name` (the leg) removed would tell the model the whole leg is gone while
      // the booked flight sits untouched.
      eventBus?.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [buildItemDeletedActivityEntry(transport.name, 'trip_planner.itinerary.canvas.flight_delete', occurredAt)],
      })
    }
  }, [actionsDisabled, onDeleteTarget, eventBus, transport, bookedFlightOption])

  const handleDeleteBookedFlight = useCallback(() => {
    if (bookedFlightOption) handleDeleteItem(bookedFlightOption.itemId)
  }, [bookedFlightOption, handleDeleteItem])

  const headingName = title ?? transport.name

  const modeOptions = useMemo(
    () => getFlightTransportDropdownOptions(transport.options),
    [transport.options],
  )
  const alternativeOptions = useMemo(
    () => modeOptions.filter((option) => {
      if (option.itemId === defaultOption.itemId) return false
      // Selected fare cards are already the flight mode. Don't also offer the
      // demoted chat_flight placeholder as "alternatives: Flight".
      if (
        isFlightCardSavedItemData(defaultOption.savedItemData) &&
        isChatFlightSavedItemData(option.savedItemData)
      ) {
        return false
      }
      return true
    }),
    [defaultOption, modeOptions],
  )
  const typeLabel = getTransportOptionLabel(defaultOption)
  const normalizedType = defaultOption.type.toLowerCase().replace(/[_\s]/g, '')
  const normalizedTypeLabel = typeLabel.toLowerCase().replace(/[_\s]/g, '')
  const isFlightTransport = normalizedType === 'flight' ||
    normalizedType === 'leflight' ||
    normalizedType === 'chatflight' ||
    normalizedTypeLabel === 'flight'
  const defaultOptionSavedItemData = defaultOption.savedItemData
  const defaultFlightSavedItemData =
    isLeFlightSavedItemData(defaultOptionSavedItemData) ||
    isChatFlightSavedItemData(defaultOptionSavedItemData) ||
    isCustomFlightSavedItemData(defaultOptionSavedItemData)
      ? defaultOptionSavedItemData
      : undefined
  const flightSearchDrawerParams = useMemo(() => {
    const params = getFlightSearchDrawerParamsForTransport(
      itineraryData?.stops ?? [],
      defaultOption,
      transport,
    )

    return applyRecentFlightSearchDefaults(
      params,
      itineraryData,
      transport.arrivalStopId ?? null,
      { preferParamsAsSelected: true },
    )
  }, [defaultOption, itineraryData, transport])

  const openFlightDrawer = useCallback(() => {
    setFlightDrawerMountKey((key) => key + 1)
    setIsFlightDrawerOpen(true)
  }, [])

  const closeFlightDrawer = useCallback(() => {
    setIsFlightDrawerOpen(false)
  }, [])

  // Selecting a flight sends a message to the assistant. Surfaces that hide the
  // conversation (the mobile timeline tab) must surface the chat so the reply is seen.
  const handleSendMessage = useMemo(() => {
    if (!onSendMessage) return undefined

    return (message: string) => {
      eventBus?.dispatch({ type: 'focus_chat' })
      onSendMessage(message)
    }
  }, [eventBus, onSendMessage])

  const mobileSearchLabel = intl.formatMessage({
    id: 'g0txbn0Fo4rljTc7oksG',
    defaultMessage: 'Search',
    description: 'Button label to submit hotel or experience search refinements in the trip planner drawer',
  })

  const browseOffersLabel = isMobile
    ? mobileSearchLabel
    : intl.formatMessage({
        id: 'h73rmlEmHCI8JbPRYjch',
        defaultMessage: 'Search flights',
        description: 'Button label to open the flight offers/search drawer from the transport line in trip planner',
      })

  const savedFlightsCountLabel = intl.formatMessage({
    id: 'wk7-MRk4wFyulX5cpkTF',
    defaultMessage: '{count, plural, one {# saved} other {# saved}}',
    description: 'Pill label showing how many flights are saved/shortlisted on a transport leg in trip planner',
  }, { count: shortlistedFlightsCount })

  const placeholderFlightDateLabel = isChatFlightSavedItemData(defaultFlightSavedItemData)
    ? getFlightDateRangeLabel(defaultFlightSavedItemData, intl)
    : undefined
  const placeholderFlightDateHasNoAlternatives = isChatFlightSavedItemData(defaultFlightSavedItemData) &&
    defaultFlightSavedItemData.plausibleCalendarDayOffsets?.length === 1
  const showLikelyPlaceholderFlightDate = Boolean(
    placeholderFlightDateLabel && !placeholderFlightDateHasNoAlternatives,
  )
  const likelyArrivalDateExplanation = showLikelyPlaceholderFlightDate && isChatFlightSavedItemData(defaultFlightSavedItemData)
    ? getLikelyArrivalDateExplanation(
        defaultFlightSavedItemData.localCalendarDayOffset,
        defaultFlightSavedItemData.plausibleCalendarDayOffsets,
        intl,
      )
    : undefined

  const stopLikelyDateInfoClick = useCallback((event: SyntheticEvent) => {
    event.stopPropagation()
  }, [])

  const alternativesList = alternativeOptions.length > 0
    ? (
        <FormattedList
          type="disjunction"
          value={alternativeOptions.map((option) => (
            <TransportAlternativeLink
              key={option.itemId}
              option={option}
              disabled={actionsDisabled}
              onSelect={handleOptionClick}
            />
          ))}
        />
      )
    : undefined

  const alternativesLabel = alternativesList
    ? intl.formatMessage({
        id: '87Jredsb9QGQIbq7dRUE',
        defaultMessage: 'alternatives: {list}',
        description: 'Inline list of other transport modes available for the same trip planner leg',
      }, { list: alternativesList })
    : undefined

  const durationLabel = formatDuration(defaultOption, intl)

  const label = (
    <Group direction="vertical" gap={2}>
      <BodyText variant="small">
        <BodyText variant="small" weight="bold" as="span">{typeLabel}</BodyText>
        {durationLabel && <>{' '}{durationLabel}</>}
      </BodyText>
      {alternativesLabel && (
        <BodyText variant="small" colour="neutral-two" as="span">
          {alternativesLabel}
        </BodyText>
      )}
      {placeholderFlightDateLabel && (
        <PlaceholderFlightDateLabel
          placeholderFlightDateLabel={placeholderFlightDateLabel}
          placeholderFlightDateHasNoAlternatives={placeholderFlightDateHasNoAlternatives}
          likelyArrivalDateExplanation={likelyArrivalDateExplanation}
          onInfoClick={stopLikelyDateInfoClick}
        />
      )}
    </Group>
  )

  const savedFlightOption = useMemo(() => {
    // Only surface a saved/shortlisted flight summary when the selected mode is a flight.
    // Otherwise train/drive selections keep getting overridden by any LE flight on the leg.
    if (!isFlightTransportOption(defaultOption)) return undefined
    if (isFlightCardSavedItemData(defaultOption.savedItemData)) return defaultOption
    return transport.options.find(o => isFlightCardSavedItemData(o.savedItemData))
  }, [defaultOption, transport.options])
  const savedFlightData = savedFlightOption && isFlightCardSavedItemData(savedFlightOption.savedItemData)
    ? savedFlightOption.savedItemData
    : undefined

  // Prefer a shortlisted flight's data; otherwise fall back to the selected flight option so the
  // tile still shows the airport-code route (e.g. "SYD → CDG") rather than a generic label.
  const summaryFlightData = savedFlightData ?? defaultFlightSavedItemData
  const summaryFlightRoute = summaryFlightData?.departureAirport && summaryFlightData.arrivalAirport
    ? formatFlightAirportRoute(
        summaryFlightData.departureAirport,
        summaryFlightData.arrivalAirport,
        intl,
      )
    : ''
  const savedFlightIsReturn = Boolean(savedFlightOption?.returnLeg ?? defaultOption.returnLeg)
  const savedFlightPrice = summaryFlightData?.farePrice ?? summaryFlightData?.price
  const savedFlightCurrency = summaryFlightData?.fareCurrencyCode ?? summaryFlightData?.currencyCode
  const savedFlightRegularPrice = summaryFlightData?.isLuxPlusFare === true
    ? summaryFlightData.regularPrice
    : undefined
  const showSavedFlightLuxPlusPrice = savedFlightPrice != null &&
    savedFlightCurrency &&
    savedFlightRegularPrice !== undefined &&
    Number.isFinite(savedFlightRegularPrice)
  // Multi-city legs are saved as separate flights sharing one group; surface the
  // combined itinerary total (stored per leg) rather than letting the per-leg
  // "From" prices read as the whole trip's flight cost. The combined total is
  // path-dependent and is not the sum of the per-leg fares.
  const multiCityGroupTotal = summaryFlightData?.flightGroupType === 'multi_city'
    ? summaryFlightData.flightGroupTotalPrice
    : undefined
  const multiCityGroupCurrency = summaryFlightData?.flightGroupTotalCurrencyCode ?? savedFlightCurrency
  const savedFlightDuration = summaryFlightData?.duration
  const savedFlightDateLabel = getSavedFlightSummaryDateLabel(summaryFlightData, savedFlightIsReturn, reversed, intl)
  // Stop count: the cached journey is the source of truth; fall back to the saved layover list.
  // Left undefined (rather than 0) when unknown, so we never show a misleading "Direct".
  const cachedJourney = summaryFlightData?.journeyKey ? getCachedJourney(summaryFlightData.journeyKey)?.journey : undefined
  const savedFlightStops = cachedJourney?.flightGroup?.numberOfStops ?? summaryFlightData?.layovers?.length

  const savedFlightPricePrefix = intl.formatMessage(savedFlightIsReturn
    ? { id: 'tripPlanSavedFlightReturnFrom', defaultMessage: 'Return from', description: 'Saved return flight price prefix in trip planner transport tile' }
    : { id: 'tripPlanSavedFlightFrom', defaultMessage: 'From', description: 'Saved one-way flight price prefix in trip planner transport tile' })
  const savedFlightPerAdult = intl.formatMessage({
    id: 'Bg2W8PA6j_4tS-XFYnVD',
    defaultMessage: '/adult',
    description: 'Per-adult price suffix on the saved flight summary in the trip planner transport tile',
  })
  const savedFlightStopsLabel = savedFlightStops != null
    ? savedFlightStops === 0 ?
        intl.formatMessage({
          id: 'pzTLHwoyrK7PPz7Obt_c',
          defaultMessage: 'Direct',
          description: 'Direct flight label on the saved flight summary in the trip planner transport tile',
        }) :
        intl.formatMessage({
          id: 'tElbzrk289iCLY79CAAD',
          defaultMessage: '{count, plural, one {# stop} other {# stops}}',
          description: 'Stop count on the saved flight summary in the trip planner transport tile',
        }, { count: savedFlightStops })
    : undefined

  const savedFlightSummary = summaryFlightRoute ? (
    <Group direction="vertical" gap={2}>
      <BodyText variant="small" weight="bold">{summaryFlightRoute}</BodyText>
      <SavedFlightMeta>
        {savedFlightPrice != null && savedFlightCurrency && (
          <SavedFlightMetaItem>
            {showSavedFlightLuxPlusPrice ? (
              <>
                <BodyText variant="small" colour="neutral-two" as="span">
                  <PricePrefix>{savedFlightPricePrefix}</PricePrefix>
                </BodyText>
                <CurrencyProvider currency={savedFlightCurrency}>
                  <SavedFlightLuxPlusPrice
                    size="S"
                    memberPrice={savedFlightPrice}
                    nonMemberPrice={savedFlightRegularPrice}
                    saleUnit="adult"
                  />
                </CurrencyProvider>
              </>
            ) : (
              <BodyText variant="small" colour="neutral-two" as="span">
                <PricePrefix>{savedFlightPricePrefix}</PricePrefix>
                <CurrencyProvider currency={savedFlightCurrency}>
                  <FormatCurrency value={savedFlightPrice} format="roundedDollar" />
                </CurrencyProvider>
                {savedFlightPerAdult}
              </BodyText>
            )}
          </SavedFlightMetaItem>
        )}
        {savedFlightDateLabel && (
          <SavedFlightMetaItem>
            <BodyText variant="small" colour="neutral-two">{savedFlightDateLabel}</BodyText>
          </SavedFlightMetaItem>
        )}
        {savedFlightDuration && (
          <SavedFlightMetaItem>
            <BodyText variant="small" colour="neutral-two">{savedFlightDuration}</BodyText>
          </SavedFlightMetaItem>
        )}
        {savedFlightStopsLabel && (
          <SavedFlightMetaItem>
            <BodyText variant="small" colour="neutral-two">{savedFlightStopsLabel}</BodyText>
          </SavedFlightMetaItem>
        )}
      </SavedFlightMeta>
      {multiCityGroupTotal != null && multiCityGroupCurrency && (
        <MultiCityTotalLabel kind="tertiary" variant="grey">
          <PricePrefix>
            {intl.formatMessage({
              id: '2qaZYohv3dcAe2-f6tzs',
              defaultMessage: 'Multi-city total',
              description: 'Badge label for the combined price of a multi-city flight itinerary in the trip planner transport tile',
            })}
          </PricePrefix>
          <CurrencyProvider currency={multiCityGroupCurrency}>
            <FormatCurrency value={multiCityGroupTotal} format="roundedDollar" />
          </CurrencyProvider>
        </MultiCityTotalLabel>
      )}
    </Group>
  ) : null

  const savedCountOrBrowseButton = hasShortlistedFlights ? (
    <SavedCountLabel variant="success">
      {savedFlightsCountLabel}
    </SavedCountLabel>
  ) : (
    <BrowseOffersButton
      kind="tertiary"
      size="small"
      startIcon={<LineSearchIcon />}
      onClick={openFlightDrawer}
    >
      {browseOffersLabel}
    </BrowseOffersButton>
  )

  const transportTile = (
    <TransportTile
      $clickable={isFlightTransport}
      $dashed={!hasShortlistedFlights}
      onClick={isFlightTransport ? openFlightDrawer : undefined}
    >
      {!hasShortlistedFlights && <TripPlanTileDashedOutline radius="M" />}
      <TileLayout $stackTrailing={hasShortlistedFlights}>
        <IconBadge className={isFlightTransport ? 'flight' : undefined}>
          {isFlightTransport ? <LinePlaneFlyIcon size="S" /> : getTransportIcon(defaultOption.type, 'S')}
        </IconBadge>
        <TransportContent direction="vertical" gap={4} fullWidth>
          {savedFlightSummary ? (
            <Group direction="vertical" gap={2}>
              {savedFlightSummary}
              {alternativesLabel && (
                <BodyText variant="small" colour="neutral-two" as="span">
                  {alternativesLabel}
                </BodyText>
              )}
            </Group>
          ) : label}
        </TransportContent>
        {isFlightTransport && (
          <TileTrailing>
            {savedCountOrBrowseButton}
          </TileTrailing>
        )}
      </TileLayout>
    </TransportTile>
  )

  const bookedFlightTileNode = bookedFlightOption ? (
    <BookedFlightTile
      option={bookedFlightOption}
      otherSavedOptions={otherSavedFlightOptions}
      onOpenDrawer={openFlightDrawer}
      onDelete={handleDeleteBookedFlight}
      reversed={reversed}
    />
  ) : null

  return (
    <TransportContainer>
      <Group direction="vertical" gap={12} fullWidth>
        <Heading variant="heading5" as="span">{headingName}</Heading>
        {bookedFlightOption && isFlightTransport ? bookedFlightTileNode : transportTile}
      </Group>
      {isFlightTransport && (
        <FlightSearchDrawer
          key={flightDrawerMountKey}
          isOpen={isFlightDrawerOpen}
          onClose={closeFlightDrawer}
          originAirportCode={flightSearchDrawerParams.originAirportCode}
          originAirportName={flightSearchDrawerParams.originAirportName}
          destinationAirportCode={flightSearchDrawerParams.destinationAirportCode}
          destinationAirportName={flightSearchDrawerParams.destinationAirportName}
          departureDate={flightSearchDrawerParams.departureDate}
          returnDate={flightSearchDrawerParams.returnDate}
          indicativeSearchMonth={itineraryData?.indicativeSearchMonth}
          tripStops={itineraryData?.stops}
          fareType={flightSearchDrawerParams.fareType}
          fareClass={flightSearchDrawerParams.fareClass}
          itinerary={flightSearchDrawerParams.itinerary}
          occupants={flightSearchDrawerParams.occupants}
          showMultiCity
          onSendMessage={handleSendMessage}
          isStreaming={isStreaming}
          existingFlights={transport.options}
          tripStopId={transport.arrivalStopId ?? null}
          hasSavedFlights={hasShortlistedFlights}
          headerSlot={hasShortlistedFlights
            ? <ShortlistedFlightsSection transport={transport} onDeleteItem={handleDeleteItem} hideHeader />
            : undefined}
        />
      )}
    </TransportContainer>
  )
}

export default FlightTripPlanTransportLine
